import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

const StorageTest = () => {
  const [user, setUser] = useState(null);
  const [testResult, setTestResult] = useState('');
  const [loading, setLoading] = useState(false);

  const addLog = useCallback((message) => {
    setTestResult(prev => prev + `\n${new Date().toLocaleTimeString()}: ${message}`);
  }, []);

  const checkUser = useCallback(async () => {
    const { data: { user } } = await supabase.auth.getUser();
    setUser(user);
    addLog(`User authenticated: ${user ? user.email : 'NOT LOGGED IN'}`);
  }, [addLog]);

  useEffect(() => {
    checkUser();
  }, [checkUser]);

  const testStorageAccess = async () => {
    setLoading(true);
    setTestResult('');

    try {
      // Test 1: Check user
      addLog('=== TEST 1: User Authentication ===');
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        addLog('❌ FAILED: No user logged in');
        addLog('⚠️  You must be logged in to upload files');
        setLoading(false);
        return;
      }
      addLog(`✅ PASSED: User logged in as ${user.email}`);

      // Test 2: Check Supabase configuration
      addLog('\n=== TEST 2: Supabase Configuration ===');
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
      if (!supabaseUrl || !supabaseKey) {
        addLog('❌ FAILED: Supabase credentials missing');
        setLoading(false);
        return;
      }
      addLog(`✅ PASSED: Supabase URL: ${supabaseUrl.substring(0, 30)}...`);

      // Test 3: Check bucket exists
      addLog('\n=== TEST 3: Check blog-images Bucket ===');
      const { data: buckets, error: bucketError } = await supabase.storage.listBuckets();
      if (bucketError) {
        addLog(`❌ FAILED: Cannot list buckets - ${bucketError.message}`);
        setLoading(false);
        return;
      }
      const blogImagesBucket = buckets.find(b => b.name === 'blog-images');
      if (!blogImagesBucket) {
        addLog('❌ FAILED: blog-images bucket does not exist');
        addLog('⚠️  Create the bucket in Supabase Dashboard → Storage');
        setLoading(false);
        return;
      }
      addLog(`✅ PASSED: blog-images bucket exists`);
      addLog(`   Public: ${blogImagesBucket.public ? 'Yes' : 'No'}`);

      // Test 4: Try to list files (tests SELECT policy)
      addLog('\n=== TEST 4: List Files (SELECT Policy) ===');
      const { data: files, error: listError } = await supabase.storage
        .from('blog-images')
        .list();

      if (listError) {
        addLog(`❌ FAILED: Cannot list files - ${listError.message}`);
        addLog('⚠️  Check SELECT policy on storage.objects for blog-images bucket');
      } else {
        addLog(`✅ PASSED: Can list files (${files.length} files found)`);
      }

      // Test 5: Try to upload a test file (tests INSERT policy)
      addLog('\n=== TEST 5: Upload Test File (INSERT Policy) ===');
      const testBlob = new Blob(['test content from storage test page'], { type: 'text/plain' });
      const testFile = new File([testBlob], 'test.txt', { type: 'text/plain' });
      const fileName = `test-${Date.now()}.txt`;

      addLog(`Attempting to upload: ${fileName}`);

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('blog-images')
        .upload(fileName, testFile);

      if (uploadError) {
        addLog(`❌ FAILED: Upload failed - ${uploadError.message}`);
        addLog(`   Error code: ${uploadError.statusCode || 'unknown'}`);
        addLog('⚠️  Check INSERT policy on storage.objects for blog-images bucket');
        addLog('⚠️  Ensure RLS is enabled and policies allow authenticated users to insert');
      } else {
        addLog(`✅ PASSED: File uploaded successfully`);
        addLog(`   Path: ${uploadData.path}`);
        addLog(`   ID: ${uploadData.id}`);

        // Test 6: Get public URL
        addLog('\n=== TEST 6: Get Public URL ===');
        const { data: urlData } = supabase.storage
          .from('blog-images')
          .getPublicUrl(fileName);

        addLog(`✅ Public URL generated:`);
        addLog(`   ${urlData.publicUrl}`);

        // Test 7: Clean up - delete test file
        addLog('\n=== TEST 7: Cleanup (DELETE Policy) ===');
        const { error: deleteError } = await supabase.storage
          .from('blog-images')
          .remove([fileName]);

        if (deleteError) {
          addLog(`⚠️  Could not delete test file - ${deleteError.message}`);
          addLog(`   This might be OK if you don't have DELETE policy`);
        } else {
          addLog(`✅ Test file deleted successfully`);
        }
      }

      addLog('\n=== TESTS COMPLETE ===');
      if (!uploadError && !listError) {
        addLog('🎉 ALL TESTS PASSED! Storage is working correctly.');
        addLog('You can now use the blog admin page to upload images.');
      } else {
        addLog('⚠️  Some tests failed. Follow the recommendations above.');
      }

    } catch (err) {
      addLog(`\n❌ UNEXPECTED ERROR: ${err.message}`);
      console.error('Test error:', err);
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#111315] text-white py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Storage RLS Test Page</h1>
          <p className="text-neutral-400">
            Use this page to diagnose Supabase Storage issues
          </p>
        </div>

        <div className="bg-[#1a1d21] border border-neutral-700 rounded-lg p-6 mb-6">
          <div className="mb-4">
            <h2 className="text-xl font-bold mb-2">Current User</h2>
            <div className="bg-[#111315] p-4 rounded-lg">
              {user ? (
                <div>
                  <p className="text-green-400">✅ Logged in as: {user.email}</p>
                  <p className="text-sm text-neutral-400 mt-1">User ID: {user.id}</p>
                </div>
              ) : (
                <div>
                  <p className="text-red-400">❌ Not logged in</p>
                  <p className="text-sm text-neutral-400 mt-1">
                    You must log in to test storage uploads
                  </p>
                </div>
              )}
            </div>
          </div>

          <button
            onClick={testStorageAccess}
            disabled={loading}
            className="w-full px-6 py-3 bg-[#CBB26A] text-black font-bold rounded-lg hover:bg-[#A8914D] transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Running Tests...' : 'Run Storage Tests'}
          </button>
        </div>

        {testResult && (
          <div className="bg-[#1a1d21] border border-neutral-700 rounded-lg p-6">
            <h2 className="text-xl font-bold mb-4">Test Results</h2>
            <pre className="bg-[#111315] p-4 rounded-lg overflow-x-auto text-sm font-mono whitespace-pre-wrap">
              {testResult}
            </pre>
          </div>
        )}

        <div className="mt-8 bg-yellow-900/20 border border-yellow-700/50 rounded-lg p-6">
          <h2 className="text-xl font-bold text-yellow-400 mb-4">Troubleshooting Guide</h2>
          <div className="space-y-3 text-sm">
            <div>
              <p className="font-bold text-yellow-300">If TEST 4 fails (Cannot list files):</p>
              <p className="text-neutral-300">Add a SELECT policy for public or authenticated users</p>
            </div>
            <div>
              <p className="font-bold text-yellow-300">If TEST 5 fails (Upload failed):</p>
              <p className="text-neutral-300">Add an INSERT policy for authenticated users</p>
            </div>
            <div>
              <p className="font-bold text-yellow-300">If you see "row-level security policy" error:</p>
              <p className="text-neutral-300">RLS is enabled but policies are missing or incorrect</p>
            </div>
            <div>
              <p className="font-bold text-yellow-300">Quick fix:</p>
              <p className="text-neutral-300">Go to Storage → blog-images → Policies → New policy → "Allow public access"</p>
            </div>
          </div>
        </div>

        <div className="mt-6 text-center">
          <a
            href="/blog/admin"
            className="text-[#CBB26A] hover:text-[#A8914D] transition underline"
          >
            ← Back to Blog Admin
          </a>
        </div>
      </div>
    </div>
  );
};

export default StorageTest;
